/**
 * BLOK 2A: PDF PARSER
 * Komunikace s backendem (Python/FastAPI) pro extrakci finančních dat z PDF
 */

import { FinancialData } from '../types';
import { authHeaders } from '../lib/apiFetch';

const API_BASE = process.env.REACT_APP_API_BASE || '';

/**
 * Nahraje PDF a extrahuje finanční data
 *
 * @param {File} pdfFile - PDF soubor z input
 * @returns {Promise<{success: boolean, data?: FinancialData, error?: string}>}
 */
export async function parsePDFWithBackend(pdfFile) {
  try {
    // Příprava FormData pro upload
    const formData = new FormData();
    formData.append('file', pdfFile);

    // Pošli na backend
    const response = await fetch(`${API_BASE}/api/parse-pdf`, {
      method: 'POST',
      headers: await authHeaders(),
      body: formData,
    });

    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      const detail = errBody.detail || response.statusText;
      throw new Error(`Chyba parsování: ${detail}`);
    }

    const result = await response.json();

    // Mapuj backend response na FinancialData
    const financialData = new FinancialData();

    financialData.year1Revenue = result.year1_revenue || 0;
    financialData.year2Revenue = result.year2_revenue || 0;
    financialData.year3Revenue = result.year3_revenue || 0;

    financialData.year1COGS = result.year1_cogs || 0;
    financialData.year2COGS = result.year2_cogs || 0;
    financialData.year3COGS = result.year3_cogs || 0;

    financialData.year1OpEx = result.year1_opex || 0;
    financialData.year2OpEx = result.year2_opex || 0;
    financialData.year3OpEx = result.year3_opex || 0;

    financialData.addBacks = result.add_backs || 0;
    financialData.inventoryValue = result.inventory_value || 0;
    financialData.statementMetrics = {
      equityValue: result.equity_value || 0,
      totalAssetsValue: result.total_assets_value || 0,
      netProfitValue: result.net_profit_value || 0,
      totalRevenueValue: result.total_revenue_value || 0,
      totalCostsValue: result.total_costs_value || 0,
      currentAssetsValue: result.current_assets_value || 0,
      shortTermLiabilitiesValue: result.short_term_liabilities_value || 0,
      shortTermFinancialAssetsValue: result.short_term_financial_assets_value || 0,
      shortTermReceivablesValue: result.short_term_receivables_value || 0,
      externalSourcesValue: result.external_sources_value || 0,
      grossProfitValue: result.gross_profit_value || 0,
      interestExpenseValue: result.interest_expense_value || 0,
      depreciationValue: result.depreciation_value || 0,
      interestValue: result.interest_value || 0,
    };

    financialData.confidence = result.confidence || 'fallback';
    financialData.source = 'pdf';

    return {
      success: true,
      data: financialData,
      confidence: result.confidence,
      extractionNotes: result.notes || '',
    };
  } catch (error) {
    console.error('PDF Parser Error:', error);
    return {
      success: false,
      error: `Chyba při parsování PDF: ${error.message}`,
    };
  }
}

/**
 * Fallback: Pokud backend není dostupný, nabídni možnost zadání ručně
 * Toto je UX fallback, ne implementace parsování
 */
export async function parseWithAI(pdfFile) {
  // Toto by mělo být pro posílání na Claude API přímo - ale v produkci
  // by měl jít request přes backend pro bezpečnost API klíče
  // Zatím vrátíme error a usmerníme na ručnícnedatování
  return {
    success: false,
    error: 'PDF parsing není zatím dostupný. Prosím, vyplňte data ručně.',
  };
}

/**
 * Validace PDF souboru
 */
export function validatePDFFile(file) {
  if (!file) {
    return { valid: false, error: 'Soubor není vybrán' };
  }

  if (file.type !== 'application/pdf') {
    return { valid: false, error: 'Soubor musí být PDF' };
  }

  const maxSize = 10 * 1024 * 1024; // 10 MB
  if (file.size > maxSize) {
    return { valid: false, error: 'Soubor je příliš velký (max 10 MB)' };
  }

  return { valid: true };
}

/**
 * Lokální extrakce textové reprezentace PDF (fallback)
 * Poznámka: Funguje bez backendu, ale je méně přesné
 */
export async function extractPDFTextLocally(pdfFile) {
  // Taby byste chtěli PDF.js nebo podobný nástroj
  // Pro MVP ignorujeme - backend parsuje
  return null;
}
